import { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { epochToTimeAgoString } from '@thoughtspot/date-format-util';
import { translate as t } from '@thoughtspot/i18n';
import {
    Icon,
    IconColor,
    IconID,
    IconSize,
} from '@thoughtspot/radiant-react/widgets/icon';
import {
    Horizontal,
    Vertical,
} from '@thoughtspot/radiant-react/widgets/layout';
import { ProgressBar } from '@thoughtspot/radiant-react/widgets/progress-bar';
import { CellType } from '@thoughtspot/radiant-react/widgets/table/table-list-view/cell/cell-type-service';
import { EmptyCell } from '@thoughtspot/radiant-react/widgets/table/table-list-view/cell/empty-cell/empty-cell';
import { MenuItem } from '@thoughtspot/radiant-react/widgets/table/table-list-view/cell/menu-cell/menu-cell';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import _ from 'lodash';
import React from 'react';
import { SpotIQListConfig } from '/@contexts/spotiq-list-context';
import {
    AllowedActions,
    AnalysisActionId,
    AnalysisActionIdInput,
    AnalysisActionStatus,
    AnalysisInfo,
    AnalysisState,
    FrequencyGranularity,
    PeriodicSchedule,
} from '/@services/generated/graphql-types';
import {
    LIST_POLL_INTERVAL,
    PROGRESS_UPDATE_FREQUENCY,
} from '../spotiq-constants';
import { getTimeToExpire } from '../spotiq-utils/spotiq-utils';
import { TranslationsKeys } from './constants';
import styles from './spotiq-list.container.module.scss';

export enum AlertVisible {
    SUCCESS,
    FAILURE,
    NONE,
}

export enum MenuItemId {
    SAVE = 'save',
    DELETE = 'delete',
    CUSTOMIZE_ANALYSIS = 'customize-analysis',
    MODIFY_ANALYSIS = 'modify-analysis',
    SCHEDULE_JOB = 'schedule-job',
    MODIFY_SCHEDULE = 'modify-schedule',
    PAUSE_JOB = 'pause-job',
    RESUME_JOB = 'resume-job',
    JOB_RUNS = 'job-runs',
}

interface TextCellType {
    text: string;
}

interface DetailsCellType {
    title: string;
    subTitle?: string;
}

export interface StatusCellType {
    text?: string;
    type: string;
    icon?: string;
    iconColor?: string;
    progress?: number;
    from?: number;
    tooltip?: string;
    granularity?: string;
}

export interface AvatarCellType {
    name: string;
    src: string;
}

export interface SpotIQTableDataType {
    id: string;
    name: DetailsCellType;
    status: StatusCellType;
    expiresIn: TextCellType;
    lastAnalyzed: TextCellType;
    triggeredBy: AvatarCellType[];
    allowedActions: AllowedActions;
    analysisId: AnalysisActionId;
    periodicSchedule: PeriodicSchedule;
    granularity?: string;
}

export interface SelectedRowIdsWithTitle {
    analysisId: AnalysisActionId;
    title: string;
}

export interface StatusCount {
    DONE?: number;
    FAILED?: number;
}

export enum Granularity {
    HOURLY = 'Hourly',
    DAILY = 'Daily',
    WEEKLY = 'Weekly',
    MONTHLY = 'Monthly',
    NONE = 'None',
}

const getTranslations = (key: string, args?: any): string =>
    t(`${key}` as any, args);

export const getAnalysisId = (
    element: AnalysisInfo | SpotIQTableDataType | AnalysisActionStatus,
): AnalysisActionIdInput | AnalysisActionId => {
    const analysisObject = {} as AnalysisActionIdInput | AnalysisActionId;
    if (element.analysisId?.analysisResultId) {
        analysisObject.analysisResultId = element.analysisId?.analysisResultId;
    }
    if (element.analysisId?.schedulerJobId) {
        analysisObject.schedulerJobId = element.analysisId?.schedulerJobId;
    }
    if (element.analysisId?.schedulerRunId) {
        analysisObject.schedulerRunId = element.analysisId?.schedulerRunId;
    }
    return analysisObject;
};

export const getGranularityForScheduler = (granularity: Granularity | null) => {
    if (!granularity) {
        return FrequencyGranularity.Daily;
    }
    switch (granularity) {
        case Granularity.DAILY:
            return FrequencyGranularity.Daily;

        case Granularity.HOURLY:
            return FrequencyGranularity.Hourly;

        case Granularity.MONTHLY:
            return FrequencyGranularity.Monthly;

        case Granularity.WEEKLY:
            return FrequencyGranularity.Weekly;

        default:
            return FrequencyGranularity.Daily;
    }
};

const getAnalysisStatus = (status: AnalysisInfo, progressFrom: number) => {
    let granularity = getTranslations(TranslationsKeys.SCHEDULE_NONE);
    if (Granularity.HOURLY === status?.granularity) {
        granularity = getTranslations(TranslationsKeys.SCHEDULE_HOURLY);
    } else if (Granularity.DAILY === status?.granularity) {
        granularity = getTranslations(TranslationsKeys.SCHEDULE_DAILY);
    } else if (Granularity.WEEKLY === status?.granularity) {
        granularity = getTranslations(TranslationsKeys.SCHEDULE_WEEKLY);
    } else if (Granularity.MONTHLY === status?.granularity) {
        granularity = getTranslations(TranslationsKeys.SCHEDULE_MONTHLY);
    }
    const createStatus = {
        type: status?.state,
        granularity,
    };
    if (status?.state === AnalysisState.Completed) {
        Object.assign(createStatus, {
            text: getTranslations(TranslationsKeys.COMPLETED),
            icon: IconID.CHECKMARK_CIRCLE,
            iconColor: IconColor.GREEN,
        });
    } else if (
        status?.state === AnalysisState.Scheduled ||
        status?.state === AnalysisState.Paused
    ) {
        Object.assign(createStatus, {
            text: getTranslations(TranslationsKeys[status?.state]),
            icon: IconID.CLOCK,
            iconColor: IconColor.BLUE,
        });
    } else if (status?.state === AnalysisState.Failed) {
        Object.assign(createStatus, {
            text: getTranslations(TranslationsKeys.FAILED),
            icon: IconID.EXCLAMATION_POINT_CIRCLE,
            iconColor: IconColor.RED,
            tooltip: status?.message,
        });
    } else if (status?.state === AnalysisState.Running) {
        Object.assign(createStatus, {
            progress: status.progress,
            from: progressFrom,
            tooltip: status?.message,
        });
    } else if (status?.state === AnalysisState.NoInsights) {
        Object.assign(createStatus, {
            text: getTranslations(TranslationsKeys.NO_INSIGHTS_STATE),
            icon: IconID.EXCLAMATION_POINT_CIRCLE,
            iconColor: IconColor.YELLOW,
        });
    }
    return createStatus;
};

export const getTimeToExpireShort = (expiry: number): string => {
    if (expiry < 0) {
        return t(TranslationsKeys.EXPIRY_NEVER);
    }
    const timeToExpire = Number(expiry * 1000) - Date.now();
    if (timeToExpire < 0) {
        return t(TranslationsKeys.EXPIRED);
    }
    const expiryTime = getTimeToExpire(timeToExpire);
    if (expiryTime.remainingDays) {
        return expiryTime.remainingDays <= 1
            ? `${expiryTime.remainingDays} ${t(TranslationsKeys.DAY)}`
            : t(TranslationsKeys.DAYS_COUNT, {
                  1: expiryTime.remainingDays,
              });
    }
    if (expiryTime.remainingHours) {
        return t(TranslationsKeys.HOURS_COUNT, {
            numHours: expiryTime.remainingHours,
        });
    }
    if (expiryTime.remainingMins) {
        return t(TranslationsKeys.MINUTES_COUNT, {
            numMinutes: expiryTime.remainingMins,
        });
    }
    return '';
};

export const normalizeData = (data: any[] = []): SpotIQTableDataType[] => {
    return data.map((element: any) => {
        const analysisObject = getAnalysisId(element);
        const analysisId =
            Object.keys(analysisObject).length === 1
                ? analysisObject.analysisResultId ??
                  analysisObject.schedulerJobId
                : `${element.analysisId.analysisResultId}-${element.analysisId.schedulerJobId}`;
        return {
            id: `spotiq-item-${analysisId}`,
            name: {
                title: element.title,
                subTitle: element.description,
            },
            status: {
                ...getAnalysisStatus(element, element.progressFrom ?? 0),
            },
            expiresIn: {
                text: getTimeToExpireShort(element.expiryDeadline),
            },
            lastAnalyzed: {
                text: epochToTimeAgoString(element.lastAnalyzed * 1000),
            },
            triggeredBy: [
                {
                    name: element.triggeredUserDisplayName,
                    src: `/callosum/v1/image/profile/${element.triggeredUserGuid}`,
                },
            ],
            allowedActions: {
                ...element.allowedActions,
            },
            analysisId: element.analysisId,
            periodicSchedule: element.periodicSchedule,
            granularity: element.granularity,
        };
    });
};

export const transitionProgressFrom = (
    prevList: AnalysisInfo[],
    nextList: AnalysisInfo[],
) => {
    return nextList.map(next => {
        let prevItem;
        if (next.state === AnalysisState.Running) {
            prevItem = _.find(prevList, { analysisId: next.analysisId });
        }
        return {
            ...next,
            progressFrom: prevItem?.progress ?? 0,
        };
    });
};

export const getProgressUpdater = (progress: number, from: number) => {
    return (cur: number) => {
        return (
            cur +
            (PROGRESS_UPDATE_FREQUENCY * (progress - from)) / LIST_POLL_INTERVAL
        );
    };
};

export const getColumnDefs = (): ColDef[] => {
    return [
        {
            headerName: getTranslations(TranslationsKeys.NAME),
            cellRendererFramework: (params: ICellRendererParams) => {
                const { title, subTitle } = params.data.name
                    ? params.data.name
                    : { title: '', subTitle: '' };
                return (
                    <Vertical className={styles.insightsNameContainer}>
                        <Typography
                            variant="content-label-subhead"
                            noMargin
                            ellipsis={{ rows: 1 }}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="footnote"
                            noMargin
                            ellipsis={{ rows: 2 }}
                        >
                            {subTitle}
                        </Typography>
                    </Vertical>
                );
            },
            colId: 'name',
            field: 'name',
            sortable: true,
            flex: 5,
            suppressMovable: false,
        },
        {
            cellRendererFramework: (params: any) => (
                <EmptyCell {...params}>
                    {({
                        text,
                        type,
                        icon,
                        iconColor,
                        progress,
                        from,
                        tooltip,
                        granularity,
                    }) => {
                        return (
                            <Vertical
                                className={styles.insightsStatus}
                                data-tooltip-content={tooltip}
                                data-tooltip-position="top"
                                data-tooltip-align="center"
                            >
                                {type === AnalysisState.Running ? (
                                    <Horizontal
                                        spacing="c"
                                        className={styles.progressBarContainer}
                                        aria-label={t('In_progress')}
                                    >
                                        <ProgressBar
                                            progress={progress}
                                            start={from}
                                            updateMillis={
                                                PROGRESS_UPDATE_FREQUENCY
                                            }
                                            progressUpdater={getProgressUpdater(
                                                progress,
                                                from,
                                            )}
                                            className={styles.progressBarWidth}
                                        />
                                    </Horizontal>
                                ) : (
                                    <Horizontal spacing="c">
                                        <Icon
                                            id={icon}
                                            color={iconColor}
                                            size={IconSize.MEDIUM}
                                            aria-label={`icon-${icon}`}
                                        />
                                        <Typography
                                            variant="body-normal"
                                            noMargin
                                        >
                                            {text}
                                        </Typography>
                                        {!_.isEmpty(tooltip) ? (
                                            <Horizontal marginLeft={12}>
                                                <Icon
                                                    id={
                                                        IconID.INFORMATION_CIRCLE
                                                    }
                                                    color={IconColor.GRAY}
                                                    size={IconSize.MEDIUM}
                                                    aria-label={t(
                                                        'information_circle_icon',
                                                    )}
                                                />
                                            </Horizontal>
                                        ) : null}
                                    </Horizontal>
                                )}
                                <Horizontal>
                                    <Typography variant="footnote" noMargin>
                                        {granularity !== 'None' &&
                                            `${t(
                                                TranslationsKeys.SCHEDULE,
                                            )}: ${granularity}`}
                                    </Typography>
                                </Horizontal>
                            </Vertical>
                        );
                    }}
                </EmptyCell>
            ),
            headerName: getTranslations(TranslationsKeys.STATUS),
            colId: 'status',
            field: 'status',
            flex: 2,
            suppressMovable: false,
        },
        {
            cellRenderer: CellType[CellType.text],
            headerName: getTranslations(TranslationsKeys.EXPIRES_IN),
            colId: 'expiresIn',
            field: 'expiresIn',
            flex: 2,
            suppressMovable: false,
        },

        {
            cellRenderer: CellType[CellType.text],
            headerName: getTranslations(TranslationsKeys.LAST_ANALYZED),
            colId: 'lastAnalyzed',
            field: 'lastAnalyzed',
            flex: 2,
            suppressMovable: false,
        },
        {
            cellRenderer: CellType[CellType.avatar],
            headerName: getTranslations(TranslationsKeys.TRIGGERED_BY),
            colId: 'triggeredBy',
            field: 'triggeredBy',
            flex: 3,
            suppressMovable: false,
        },
    ];
};

export const getRowMenuConfig = (
    type: MenuItemId,
    row: SpotIQTableDataType,
    onMenuClick: (menuId: string, row: SpotIQTableDataType) => void,
): MenuItem => {
    switch (type) {
        case MenuItemId.SAVE:
            return {
                id: type,
                label: getTranslations(TranslationsKeys.SAVE),
                icon: IconID.SAVE,
                onClick: () => onMenuClick(type, row),
            };
        case MenuItemId.DELETE:
            return {
                id: type,
                label: getTranslations(TranslationsKeys.DELETE),
                icon: IconID.TRASH_CAN,
                onClick: () => onMenuClick(type, row),
            };
        case MenuItemId.CUSTOMIZE_ANALYSIS: {
            const updatedType = row?.allowedActions?.updateAnalysis
                ? MenuItemId.MODIFY_ANALYSIS
                : MenuItemId.CUSTOMIZE_ANALYSIS;
            return {
                id: updatedType,
                label: row?.allowedActions?.updateAnalysis
                    ? getTranslations(TranslationsKeys.CUSTOMIZE_ANALYSIS)
                    : getTranslations(
                          TranslationsKeys.CUSTOMIZE_AND_RUN_ANALYSIS,
                      ),
                icon: IconID.COG,
                onClick: () => onMenuClick(updatedType, row),
            };
        }

        case MenuItemId.SCHEDULE_JOB: {
            const modifiedType = row?.allowedActions?.modifySchedule
                ? MenuItemId.MODIFY_SCHEDULE
                : MenuItemId.SCHEDULE_JOB;

            return {
                id: type,
                label: row?.allowedActions?.modifySchedule
                    ? getTranslations(TranslationsKeys.MODIFY_SCHEDULE)
                    : getTranslations(TranslationsKeys.SCHEDULE_ANALYSIS),
                icon: IconID.CLOCK,
                onClick: () => onMenuClick(modifiedType, row),
            };
        }

        case MenuItemId.PAUSE_JOB:
            return {
                id: type,
                label: getTranslations(TranslationsKeys.PAUSE_SCHEDULE),
                icon: IconID.PAUSE,
                onClick: () => onMenuClick(type, row),
            };
        case MenuItemId.RESUME_JOB:
            return {
                id: type,
                label: getTranslations(TranslationsKeys.RESUME_SCHEDULE),
                icon: IconID.PLAY,
                onClick: () => onMenuClick(type, row),
            };
        case MenuItemId.JOB_RUNS:
            return {
                id: type,
                label: getTranslations(TranslationsKeys.JOB_SCHEDULE),
                icon: IconID.SORT,
                onClick: () => onMenuClick(type, row),
            };
        default:
            return {} as MenuItem;
    }
};

export const parseData = (data: AnalysisInfo[]) => {
    return [
        ...new Set(
            data.map(
                (element: AnalysisInfo): AnalysisActionIdInput =>
                    getAnalysisId(element),
            ),
        ),
    ];
};

// for Delete
export const getDeletePostData = (
    selectedRowsIds: string[],
    entries: SpotIQTableDataType[],
): AnalysisActionIdInput[] => {
    return selectedRowsIds.map((id: string) => {
        return {
            ...entries
                .map((item: SpotIQTableDataType) => {
                    const deletePostObject = {};
                    if (item.id === id) {
                        const analysisId = getAnalysisId(item);
                        Object.assign(deletePostObject, {
                            ...analysisId,
                        });
                    }
                    return deletePostObject;
                })
                .filter(
                    (deletePostObject: any) => !_.isEmpty(deletePostObject),
                )[0],
        };
    });
};

export const getSelectedRowIdsWithTitle = (
    selectedRowsIds: string[],
    entries: SpotIQTableDataType[],
) => {
    return selectedRowsIds.map((id: string) => {
        return {
            ...entries
                .map((item: SpotIQTableDataType) => {
                    const selectedRowIdsWithTitle = {};
                    if (item.id === id) {
                        Object.assign(selectedRowIdsWithTitle, {
                            analysisId: getAnalysisId(item),
                            title: item.name.title,
                        });
                    }
                    return selectedRowIdsWithTitle;
                })
                .filter(
                    (selectedRowIdsWithTitle: any) =>
                        !_.isEmpty(selectedRowIdsWithTitle),
                )[0],
        };
    });
};

export const getParamsFromState = (state: SpotIQListConfig) => ({
    batchSize: state.batchSize,
    offset: state.offset,
    sortBy: state.sortBy,
    sortReversed: state.sortReversed,
    searchPattern: state.searchPattern,
    triggeredByCurrentUser: state.triggeredByCurrentUser,
});
